// Die Kistenansicht (task-014, docs/architektur-paket-2.md, Abschnitt „Client“, `hud/`): oben die
// sechzehn Fächer der eigenen Kiste, unten die Leiste. Ein Klick auf einen Stapel legt ihn auf die
// andere Seite, Escape oder E schließt.
//
// Entschieden wird hier nichts. Ob die Kiste offen ist, was in den Fächern liegt und welcher Klick
// hinausgeht, weiß game/kiste.ts, und gebucht wird auf dem Server. Die Ansicht rührt das DOM nur an,
// wenn sich etwas geändert hat, damit der Aufruf je Bild nichts kostet. Die Fächer sind dieselben wie
// in der Leiste am Bildrand, nur als Knöpfe.

import { KISTE_FAECHER, type ChestSide, type ItemStack, type Kistenfach } from '@bauernhof/shared';
import { HOTBAR_SLOTS, MELDUNG_DAUER_MS, fachAnsicht, hotbarSlots, type FachAnsicht, type HotbarSlot } from './hud.js';

export interface KistenansichtOptionen {
  /** Ein Klick auf ein Fach der Kiste (`'kiste'`) oder der Leiste (`'leiste'`). */
  beiKlick(from: ChestSide, index: number): void;
  /** Escape, E oder der Knopf „Schließen“. */
  beiSchliessen(): void;
}

/** Was die Ansicht zeigt. */
export interface KistenStand {
  offen: boolean;
  faecher: readonly Kistenfach[];
  /** Die Leiste, also die Stapel des Inventars. */
  stacks: readonly ItemStack[];
}

export interface Kistenansicht {
  /** Bringt die Ansicht auf den Stand; darf je Bild aufgerufen werden. */
  zeige(stand: KistenStand): void;
  /** Ein kurzer Satz unter den Fächern, etwa „Die Kiste ist voll.“; er verschwindet von selbst. */
  zeigeMeldung(text: string): void;
  dispose(): void;
}

export function createKistenansicht(parent: HTMLElement, optionen: KistenansichtOptionen): Kistenansicht {
  const wurzel = document.createElement('div');
  wurzel.className = 'kiste';
  wurzel.hidden = true;

  const karte = document.createElement('div');
  karte.className = 'kiste-karte';
  karte.setAttribute('role', 'dialog');
  karte.setAttribute('aria-label', 'Deine Kiste');

  const titel = document.createElement('h1');
  titel.textContent = 'Deine Kiste';

  const kistenGitter = document.createElement('div');
  kistenGitter.className = 'kiste-faecher';
  const kistenFaecher = baueFaecher(kistenGitter, KISTE_FAECHER, 'kiste', 'Kiste');

  const leisteTitel = document.createElement('h2');
  leisteTitel.textContent = 'Deine Leiste';

  const leisteReihe = document.createElement('div');
  leisteReihe.className = 'kiste-leiste';
  const leisteFaecher = baueFaecher(leisteReihe, HOTBAR_SLOTS, 'leiste', 'Leiste');

  const anleitung = document.createElement('p');
  anleitung.className = 'kiste-anleitung';
  anleitung.textContent = 'Klicke auf einen Stapel, dann wandert er hinüber. Escape oder E schließt die Kiste.';

  const meldung = document.createElement('p');
  meldung.className = 'kiste-meldung';
  meldung.setAttribute('role', 'status');
  meldung.hidden = true;

  const schliessen = document.createElement('button');
  schliessen.type = 'button';
  schliessen.className = 'kiste-knopf';
  schliessen.textContent = 'Schließen';
  schliessen.addEventListener('click', () => optionen.beiSchliessen());

  karte.append(titel, kistenGitter, leisteTitel, leisteReihe, anleitung, meldung, schliessen);
  wurzel.append(karte);
  parent.append(wurzel);

  // Escape oder E schließt. Die Maus ist frei, solange die Kiste offen ist; die Tasten des Spiels
  // kommen dann nicht an, darum hört die Ansicht selbst zu.
  const beiTaste = (ereignis: KeyboardEvent): void => {
    // Ein gehaltenes E, das die Kiste gerade geöffnet hat, schickt Wiederholungen hinterher; die
    // dürfen sie nicht sofort wieder schließen. Nur ein neuer Druck schließt.
    if (wurzel.hidden || ereignis.repeat || (ereignis.code !== 'Escape' && ereignis.code !== 'KeyE')) return;
    ereignis.preventDefault();
    ereignis.stopPropagation();
    optionen.beiSchliessen();
  };
  window.addEventListener('keydown', beiTaste);

  let letzteKiste: HotbarSlot[] = kistenFaecher.map(() => ({ item: null, count: 0 }));
  let letzteLeiste: HotbarSlot[] = leisteFaecher.map(() => ({ item: null, count: 0 }));
  let meldungTimer: ReturnType<typeof setTimeout> | null = null;

  function baueFaecher(behaelter: HTMLElement, anzahl: number, seite: ChestSide, name: string): FachAnsicht[] {
    const liste: FachAnsicht[] = [];
    for (let index = 0; index < anzahl; index++) {
      const fach = fachAnsicht('button');
      fach.wurzel.setAttribute('aria-label', `${name}, Fach ${index + 1}`);
      fach.wurzel.addEventListener('click', () => optionen.beiKlick(seite, index));
      behaelter.append(fach.wurzel);
      liste.push(fach);
    }
    return liste;
  }

  /** Zeichnet nur die Fächer neu, deren Inhalt sich geändert hat. */
  function aktualisiere(ansichten: FachAnsicht[], alt: HotbarSlot[], neu: HotbarSlot[]): void {
    neu.forEach((fach, index) => {
      const vorher = alt[index];
      const ansicht = ansichten[index];
      if (ansicht === undefined || (vorher !== undefined && vorher.item === fach.item && vorher.count === fach.count)) return;
      if (vorher?.item !== fach.item) ansicht.setItem(fach.item);
      ansicht.setCount(fach.count);
    });
  }

  return {
    zeige(stand) {
      if (wurzel.hidden === stand.offen) wurzel.hidden = !stand.offen;
      if (!stand.offen) return;
      const kiste = kistenFaecher.map((_, index): HotbarSlot => {
        const fach = stand.faecher[index] ?? null;
        return fach === null ? { item: null, count: 0 } : { item: fach.item, count: fach.count };
      });
      aktualisiere(kistenFaecher, letzteKiste, kiste);
      letzteKiste = kiste;
      const leiste = hotbarSlots({ stacks: [...stand.stacks] }, leisteFaecher.length);
      aktualisiere(leisteFaecher, letzteLeiste, leiste);
      letzteLeiste = leiste;
    },

    zeigeMeldung(text) {
      if (meldungTimer !== null) clearTimeout(meldungTimer);
      meldung.textContent = text;
      meldung.hidden = false;
      meldungTimer = setTimeout(() => {
        meldung.hidden = true;
        meldungTimer = null;
      }, MELDUNG_DAUER_MS);
    },

    dispose() {
      if (meldungTimer !== null) clearTimeout(meldungTimer);
      window.removeEventListener('keydown', beiTaste);
      wurzel.remove();
    },
  };
}
